import { ChangeEvent, useEffect, useState } from "react";

import { Autocomplete, SelectEvent } from "@king-ict/eupisi-ui/components";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";

import { handleGetInstitutionListQuery } from "./utils";
import { InstitutionCodeBook } from "../../models";

interface IProps {
  value?: number;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  label?: string;
  errorText?: string;
  countyId?: number;
  countyIds?: number[];
  municipalityId?: number;
  townId?: number;
  townIds?: number[];
  ids?: number[];
  onlyByEnrollmentArea?: boolean;
  onlyByEnrollmentInstitution?: boolean;
  excludeHandicapped?: boolean;
  excludeSatellite?: boolean;
  excludePrivate?: boolean;
  excludeIds?: number[];
  excludeHasRegionalCommittee?: boolean;
  showUser?: boolean;
  allowEmpty?: boolean;

  onChange: (event: ChangeEvent<SelectEvent>) => void;
  onLoaded?: (selectedInstitution?: InstitutionCodeBook[]) => void;
  shouldCheckParentCounty?: boolean;
}

/**
 * This is a single select component based on an AutoComplete
 * (combobox - text + dropdown) for Institutions and may be
 * used where an InstitutionId is optional. To change the selection,
 * the current selection must be cleared by clicking the 'X', afterwhich
 * a new selection may be made. Between selections, the selected value
 * will be undefined.
 * @param param0
 * @returns
 */
export const InstitutionSelect = ({
  showUser = false,
  value,
  label,
  disabled = false,
  errorText,
  countyId,
  countyIds,
  municipalityId,
  townId,
  townIds,
  ids,
  allowEmpty = false,
  onlyByEnrollmentArea = false,
  onlyByEnrollmentInstitution = false,
  excludeIds,
  onChange,
  onLoaded,
  excludeHandicapped = false,
  excludeSatellite = false,
  excludePrivate = false,
  excludeHasRegionalCommittee = false,
  shouldCheckParentCounty,
}: IProps) => {
  const [institutions, setFilteredInstitutions] =
    useState<InstitutionCodeBook[]>();
  const { data: institutionList } = handleGetInstitutionListQuery(showUser);

  useEffect(() => {
    (async () => {
      setFilteredInstitutions(
        institutionList?.filter((institution) => {
          const conditions = [
            {
              check: countyIds,
              valid: () =>
                countyIds?.includes(institution.countyId) ||
                (shouldCheckParentCounty &&
                  countyIds?.includes(institution.parentCountyId ?? 0)),
            },
            {
              check: countyId,
              valid: () =>
                institution.countyId === countyId ||
                (shouldCheckParentCounty &&
                  institution.parentCountyId === countyId),
            },
            {
              check: municipalityId,
              valid: () => institution.municipalityId === municipalityId,
            },
            {
              check: townIds,
              valid: () => townIds?.includes(institution.townId),
            },
            { check: townId, valid: () => institution.townId === townId },
            { check: ids, valid: () => ids?.includes(institution.id) },

            {
              check: onlyByEnrollmentArea,
              valid: () => institution.isByEnrollmentArea,
            },
            {
              check: onlyByEnrollmentInstitution,
              valid: () => institution.isEnrollmentInstitution,
            },
            {
              check: excludeIds,
              valid: () => !excludeIds?.includes(institution.id),
            },

            {
              check: excludeHandicapped,
              valid: () => !institution.isHandicapped,
            },
            { check: excludeSatellite, valid: () => !institution.isSatellite },
            { check: excludePrivate, valid: () => !institution.isPrivate },
            {
              check: excludeHasRegionalCommittee,
              valid: () => !institution.hasRegionalCommittee,
            },
          ];
          return conditions.every(({ check, valid }) => !check || valid());
        })
      );
    })();
  }, [
    countyId,
    countyIds,
    townId,
    townIds,
    ids,
    municipalityId,
    onlyByEnrollmentArea,
    onlyByEnrollmentInstitution,
    excludeIds,
    institutionList,
    excludeHandicapped,
    excludeSatellite,
    excludePrivate,
    excludeHasRegionalCommittee,
    shouldCheckParentCounty,
  ]);

  useEffect(() => {
    if (institutions) {
      if (onLoaded) {
        onLoaded(institutions);
      }

      // Are we forcing a value?
      if (!allowEmpty && institutions.length > 0) {
        if (
          institutions.length === 1 || // We only have one so use it
          value === undefined || // We must set something
          !institutions.some((v) => v.id === value)
        ) {
          // The desired value is not in list
          value = institutions[0].id;
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutions]);

  return (
    <Autocomplete
      label={label}
      name={"InstitutionId"}
      value={value}
      disabled={disabled}
      selectData={codeBookToSelectData(institutions)}
      onChange={onChange}
      errorText={errorText}
    />
  );
};
